import React, { useState } from 'react';
import { UserRole, isDemo } from '../types';
import { ICONS } from '../constants';
import Button from './ui/Button';
import LanguageToggle from './LanguageToggle';
import InviteButton from './InviteButton';
import { useTranslation } from '../hooks/useTranslation';
import Avatar from './Avatar';
import { apiPost } from '../utils/api';
import { API_CONFIG, SSO_CONFIG } from '../config';

interface HeaderProps {
  currentRole: string | null;
  currentUser: { email: string; username?: string; role: string; id?: string } | null;
  onLogout: () => void;
  onAddArtist: () => void;
  onManageProjects: () => void;
  onExportExcel: () => void;
  onBackupManagement: () => void;
  selectedCount: number;
  currentView?: 'users' | 'artists' | 'projects' | 'teams' | 'rates' | 'bidding' | 'finance';  // Добавлено для управления Export
  activeUsers?: Array<{ userId: string; username: string; email: string; currentView?: string; biddingSheetId?: string | null }>;
  currentBiddingSheetId?: string | null;
}

const Header: React.FC<HeaderProps> = ({ currentRole, currentUser, onLogout, onAddArtist, onManageProjects, onExportExcel, onBackupManagement, selectedCount, currentView, activeUsers = [], currentBiddingSheetId }) => {
  const { t } = useTranslation();
  const [isLoadingMagicBox, setIsLoadingMagicBox] = useState(false);

  const exportButtonText = selectedCount > 0 
    ? t('header.exportSelected', { count: selectedCount })
    : t('header.export');

  // Convert UserRole enum to display string
  const getRoleDisplayName = (role: string | null): string => {
    switch (role) {
      case 'Super Admin':
        return t('roles.Super Admin');
      case 'Admin':
        return t('roles.Admin');
      case 'Producer':
        return t('roles.Producer');
      case 'Project Manager':
        return t('roles.Project Manager');
      case 'Coordinator':
        return t('roles.Coordinator');
      case 'Demo':
        return t('roles.Demo');
      default:
        return 'User';
    }
  };

  // Фильтруем активных пользователей: исключаем текущего пользователя
  const otherActiveUsers = activeUsers.filter(
    (u) => u.userId !== currentUser?.id
  );

  // Разделяем на тех, кто на том же view/sheet (цветные) и на других (чёрно-белые)
  const sameContextUsers = otherActiveUsers.filter((u) => {
    // Если мы на bidding sheet, проверяем точное совпадение sheet
    if (currentView === 'bidding' && currentBiddingSheetId) {
      return u.currentView === 'bidding' && u.biddingSheetId === currentBiddingSheetId;
    }
    // Если мы на bidding view но без открытой sheet, показываем пользователей на том же bidding view без sheet как цветные
    if (currentView === 'bidding' && !currentBiddingSheetId) {
      return u.currentView === 'bidding' && !u.biddingSheetId; // null или undefined
    }
    // Если мы на обычном view (users/projects), проверяем совпадение view
    if (currentView && currentView !== 'bidding') {
      return u.currentView === currentView;
    }
    return false;
  });

  const handleGoToMagicBox = async () => {
    // Check if MagicBox URL is configured
    const magicboxUrl = SSO_CONFIG.MAGICBOX_URL;
    
    if (!magicboxUrl) {
      console.log('MagicBox URL not configured - button should be hidden');
      return;
    }

    setIsLoadingMagicBox(true);
    try {
      const response = await apiPost(`${API_CONFIG.API_URL}/auth/cross-token`, { targetSystem: 'magicbox' });
      const data = await response.json();

      // Open MagicBox in same window with cross-auth token (seamless SSO)
      const url = new URL('/cross-login', magicboxUrl);
      url.searchParams.set('token', data.crossToken);
      window.location.href = url.href;
    } catch (error: any) {
      console.error('Error generating cross-auth token:', error);
      alert(error.message || t('header.magicboxError'));
    } finally {
      setIsLoadingMagicBox(false);
    }
  };

  const differentContextUsers = otherActiveUsers.filter((u) => {
    // Если мы на bidding sheet, все остальные - разные контексты
    if (currentView === 'bidding' && currentBiddingSheetId) {
      return !(u.currentView === 'bidding' && u.biddingSheetId === currentBiddingSheetId);
    }
    // Если мы на bidding view но без открытой sheet, разные контексты - это те, кто НЕ на bidding или на bidding но с другим sheet
    if (currentView === 'bidding' && !currentBiddingSheetId) {
      return !(u.currentView === 'bidding' && !u.biddingSheetId);
    }
    // Если мы на обычном view, все кто не на этом view - разные контексты
    if (currentView && currentView !== 'bidding') {
      return u.currentView !== currentView;
    }
    // Все остальные - разные контексты
    return true;
  });

  return (
    <header className="bg-gray-800 shadow-md sticky top-0 z-30">
      <div className="px-4 md:px-8 py-3 sm:py-4 flex justify-between items-center">
        {/* Logo */}
        <div className="flex items-center space-x-2">
          <ICONS.BookOpen className="w-6 h-6 sm:w-8 sm:h-8 text-indigo-400" />
          <h1 className="text-lg sm:text-2xl font-bold text-white tracking-wider">
            VFX SpellBook
          </h1>
        </div>

        {/* Buttons */}
        <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-4">
          {/* Active users avatars */}
          {otherActiveUsers.length > 0 && (
            <div className="hidden md:flex items-center space-x-1">
              {/* Цветные аватары - пользователи на том же view/sheet */}
              {sameContextUsers.map((u) => (
                <Avatar
                  key={u.userId}
                  name={u.username || u.email}
                  email={u.email}
                  size="sm"
                  className="border-2 border-green-500"
                />
              ))}
              {/* Чёрно-белые аватары - пользователи на других view/sheet */}
              {differentContextUsers.map((u) => (
                <Avatar
                  key={u.userId}
                  name={u.username || u.email}
                  email={u.email}
                  size="sm"
                  className="border-2 border-gray-500 grayscale opacity-70"
                />
              ))}
            </div>
          )}

          {/* User info - скрываем на мобильных */}
          <div className="hidden md:flex items-center space-x-2">
            <ICONS.User className="w-6 h-6 text-gray-400" />
            <span className="text-white font-medium">
              {currentUser?.username || currentUser?.email || getRoleDisplayName(currentRole)}
            </span>
          </div>

          {/* Export - для вкладки "Профили" (скрыта для Demo) */}
          {currentRole !== 'Demo' && currentView === 'users' && (
            <Button 
              onClick={onExportExcel} 
              variant="secondary" 
              className="flex items-center space-x-0 sm:space-x-2 !p-2 sm:!px-4 sm:!py-2"
              title={exportButtonText}
            >
              <ICONS.Export className="w-5 h-5" />
              <span className="hidden sm:inline">{exportButtonText}</span>
            </Button>
          )}

          {/* Add Project - скрыта для Demo пользователей и на вью "команды" */}
          {currentRole !== 'Demo' && currentView !== 'teams' && (
            <Button 
              onClick={onManageProjects} 
              variant="secondary" 
              className="flex items-center space-x-0 sm:space-x-2 !p-2 sm:!px-4 sm:!py-2"
              title={t('header.addProject')}
            >
              <ICONS.Briefcase className="w-5 h-5" />
              <span className="hidden sm:inline">{t('header.addProject')}</span>
            </Button>
          )}

          {/* Add Profile - скрыта для Demo пользователей */}
          {currentRole !== 'Demo' && (
            <Button 
              onClick={onAddArtist} 
              variant="secondary"
              className="flex items-center space-x-0 sm:space-x-2 !p-2 sm:!px-4 sm:!py-2"
              title={t('header.addProfile')}
            >
              <ICONS.User className="w-5 h-5" />
              <span className="hidden sm:inline">{t('header.addProfile')}</span>
            </Button>
          )}
          
          {/* Invite Button - создание публичной анкеты */}
          <InviteButton userRole={currentUser?.role || ''} />
          
          {/* Language Toggle */}
          <LanguageToggle />
          
          {/* System Menu - скрыта для Demo и Client пользователей */}
          {currentRole !== 'Demo' && currentRole !== 'Client' && (
            <Button 
              onClick={onBackupManagement} 
              variant="secondary" 
              className="!p-2" 
              title={t('system.title')}
            >
              <ICONS.Settings className="w-5 h-5" />
            </Button>
          )}
          
          {/* MagicBox Link - переход в MagicBox через SSO (рядом с Logout, сиреневая) */}
          {SSO_CONFIG.MAGICBOX_URL && (
            <Button 
              onClick={handleGoToMagicBox}
              className="!bg-purple-600 hover:!bg-purple-700 flex items-center space-x-0 sm:space-x-2 !p-2 sm:!px-4 sm:!py-2"
              disabled={isLoadingMagicBox}
              title={t('header.goToMagicBox')}
            >
              <ICONS.Link className="w-5 h-5" />
              <span className="hidden sm:inline">{t('header.goToMagicBox')}</span>
            </Button>
          )}
          
          {/* Logout - только иконка на мобильных */}
          <Button 
            onClick={onLogout} 
            variant="danger" 
            className="bg-red-600 hover:bg-red-700 text-white flex items-center space-x-0 sm:space-x-2 !p-2 sm:!px-4 sm:!py-2"
            title={t('header.logout')}
          >
            <ICONS.X className="w-5 h-5" />
            <span className="hidden sm:inline">{t('header.logout')}</span>
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Header;